import React, { useRef, useState } from 'react';
import { ReactComponent as ExportIcon } from '../../../../../assets/icons/export-icon-gray.svg';
import { ReactComponent as DownloadZipIcon } from '../../../../../assets/icons/download_zip.svg';
import { useDispatch, useSelector } from 'react-redux';
import { checkPermission } from '../../../../../helpers/checkPermissions';
import permissions from '../../../../../config/permissions';
import { REQUEST_STATUS } from '../../../../../redux_store/constants';
import { useParams } from 'react-router-dom';
import { useMutation } from 'react-fetching-library';
import showServerError from '../../../../../helpers/showError';
import { getFormattedDate } from '../../../../../helpers/localize';
import { makeStyles } from '@material-ui/core';
import ExportCSVButtonLink from './widgets/ExportCSVButtonLink';
import MenuItemWithDialog from '../../../../Shared/Table/widgets/TopBar/MenuItem/MenuItemWithDialog';
import RoundedButton from '../../../../Shared/RoundedButton';

const useStyles = makeStyles((theme) => ({
  selectorMenu: {
    padding: 3,
    fontWeight: 400,
    maxWidth: 220,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    overflow: 'hidden',
    fontSize: '1rem',
    boxSizing: 'border-box',
    lineHeight: 1.5,
    letterSpacing: '0.00938em',
  },
  icon: {
    width: 22,
    height: 22,
  },
  iconExport: {
    transform: 'rotate( -180deg )',
    width: 20,
    height: 20,
  },
  driver: {
    borderTop: `1px solid ${theme.palette.text.secondary}`,
    opacity: 0.2,
  },
  description: {
    maxWidth: 200,
    paddingBottom: 12,
  },
  option: {
    justifyContent: 'flex-start',
  },
}));

const TableActions = ({
                        fetchCSVData,
                        CSVDataLoadingStatus,
                        CSVData,
                        itemLength,
                      }) => {
  const { en } = useSelector((state) => state.i18n);
  const { tables: tTables, btn: tBtn } = en;
  const classes = useStyles();
  const dispatch = useDispatch();
  const downloadCSVLink = useRef();
  const { accountUuid: paramAccountUuid } = useParams();
  const {
    en: { notifications: tNotifications, titles: tTitles },
  } = useSelector((state) => state.i18n);
  const {
    metadata: { currentAccountPermissions, currentAccount },
  } = useSelector((state) => state.user);
  const [isOpenDialog, setOpenDialog] = useState(false);
  const [isOpenZIPDialog, setOpenZIPDialog] = useState(false);
  const [exporting, setExporting] = useState();
  const [downloadingZIPStatus, setDownloadingZIPStatus] = useState(
    REQUEST_STATUS.idle,
  );
  const { user } = useSelector((state) => state.user);
  const hasLoadFilesPermission = checkPermission(
    currentAccountPermissions,
    permissions.ADMIN_PATIENT_IMPORT,
  );

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setOpenZIPDialog(false);
  };

  const downloadedCSVFile = async () => {
    fetchCSVData();
    handleCloseDialog();
    setExporting(true);
  };

  const getClaimsZIP = () => ({
    method: 'GET',
    endpoint: `/accounts/${
      paramAccountUuid || currentAccount.uuid
    }/orders/report`,
    responseType: 'blob',
    headers: {
      'Content-Type': 'application/zip',
    },
  });

  const { mutate: getZIPFile } = useMutation(getClaimsZIP);

  const downloadedZIPFile = async () => {
    setDownloadingZIPStatus(REQUEST_STATUS.loading);
    const { payload, error, status } = await getZIPFile();
    if (error) {
      setDownloadingZIPStatus(REQUEST_STATUS.failed);
      setTimeout(() => {
        setDownloadingZIPStatus(REQUEST_STATUS.idle);
      }, 2000);
      const options = payload && {
        correlationUUID: payload.correlationUUID,
        userUUID: user.uuid,
      };
      switch (status) {
        case 401: {
          showServerError(
            dispatch,
            tNotifications.notPermissionForDownloadingFile,
          );
          break;
        }
        default: {
          showServerError(dispatch, '', options);
          break;
        }
      }
    } else if (payload && payload.size > 0) {
      setDownloadingZIPStatus(REQUEST_STATUS.succeeded);
      setTimeout(() => {
        setDownloadingZIPStatus(REQUEST_STATUS.idle);
      }, 2000);
      const url = window.URL.createObjectURL(
        new Blob([payload], { type: 'application/zip' }),
      );
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `${tTitles.orders} ${getFormattedDate(new Date(Date.now()))}.zip`,
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      handleCloseDialog();
    }
  };

  return (
    <>
      {hasLoadFilesPermission && (
        <>
          <MenuItemWithDialog
            icon={<ExportIcon className={classes.iconExport} />}
            label={tTables.downloadCSVFile}
            onClick={() => setOpenDialog(true)}
            isLoading={CSVDataLoadingStatus === REQUEST_STATUS.loading}
            isSuccess={CSVDataLoadingStatus === REQUEST_STATUS.succeeded}
            isFailed={CSVDataLoadingStatus === REQUEST_STATUS.failed}
            dialogTitle={tTables.downloadFileByFilters}
            dialogSubtitle={tTables.downloadFileFilteredData}
            dialogIsOpen={isOpenDialog}
            dialogHandleClose={handleCloseDialog}
            dialogOptions={[
              {
                label: tTables.numbersOfRows,
                value: itemLength,
              },
            ]}
            dialogActions={
              <RoundedButton
                onClick={downloadedCSVFile}
                isLoading={CSVDataLoadingStatus === REQUEST_STATUS.loading}
                disabled={CSVDataLoadingStatus === REQUEST_STATUS.loading}
                variant='contained'
                color='primary'
                size='small'
                className={classes.btn}
                LoadingWrapperProps={{
                  LoaderProps: {
                    disableShrink: true,
                  },
                }}
              >
                {tBtn.downloadCSV}
              </RoundedButton>
            }
          />
          <ExportCSVButtonLink
            CSVData={CSVData}
            exporting={exporting}
            setExporting={setExporting}
            downloadCSVLink={downloadCSVLink}
          />
        </>
      )}
      {hasLoadFilesPermission && (
        <>
          <MenuItemWithDialog
            icon={<DownloadZipIcon className={classes.icon} />}
            label={tTables.downloadOrderReports}
            onClick={() => setOpenZIPDialog(true)}
            isLoading={downloadingZIPStatus === REQUEST_STATUS.loading}
            isSuccess={downloadingZIPStatus === REQUEST_STATUS.succeeded}
            isFailed={downloadingZIPStatus === REQUEST_STATUS.failed}
            dialogTitle={tTables.downloadOrderReports}
            dialogSubtitle={'ZIP includes:'}
            dialogIsOpen={isOpenZIPDialog}
            dialogHandleClose={handleCloseDialog}
            dialogOptions={[
              {
                label: `Pharmacy_Rx_${getFormattedDate(new Date(Date.now()))}.docx`,
                value: `Pharmacy Rx`,
              },
              {
                label: `Pharmacy_Orders_${getFormattedDate(new Date(Date.now()))}.xlsx`,
                value: `Pharmacy Orders Spreadsheet`,
              },
            ]}
            dialogActions={
              <RoundedButton
                onClick={downloadedZIPFile}
                isLoading={downloadingZIPStatus === REQUEST_STATUS.loading}
                disabled={downloadingZIPStatus === REQUEST_STATUS.loading}
                variant='contained'
                color='primary'
                size='small'
                className={classes.btn}
                LoadingWrapperProps={{
                  LoaderProps: {
                    disableShrink: true,
                  },
                }}
              >
                {tBtn.downloadZIP}
              </RoundedButton>
            }
          />
        </>
      )}
    </>
  );
};

export default TableActions;
